import { useMemo } from 'react';

import type { ActionResolution } from 'modules/common/components/findings';
import type { ISuggestionAnnotation } from 'modules/common/viewer/pdfViewer/annotationCommands/SuggestionAnnotation';

import {
  usePartDescriptorActionData,
  DEFAULT_PART_DESCRIPTOR_ACTIONS,
} from './PartDescriptorActionDataContext';
import type { PartDescriptorAnnotationActions } from './PartDescriptorActionDataContext';

type PartDescriptorActionsProps = {
  partDescriptorActions: PartDescriptorAnnotationActions;
};

const noOpActions = DEFAULT_PART_DESCRIPTOR_ACTIONS();

export const usePartDescriptorActions = (
  annotation: ISuggestionAnnotation | null,
): ActionResolution<PartDescriptorActionsProps> => {
  const { partDescriptorActions, processAnnotation } =
    usePartDescriptorActionData();

  const payload = useMemo(
    () => processAnnotation(annotation),
    [processAnnotation, annotation],
  );

  const canEditPartDescriptor = useMemo(() => {
    return (
      payload && (payload.partNumber || payload.revision || payload.description)
    );
  }, [payload]);

  const partDescriptorActionsFn = useMemo(
    () =>
      canEditPartDescriptor && payload
        ? partDescriptorActions(payload)
        : noOpActions,
    [partDescriptorActions, payload, canEditPartDescriptor],
  );

  if (!canEditPartDescriptor) {
    return {
      enabled: false,
      partDescriptorActions: partDescriptorActionsFn,
    };
  }

  return {
    enabled: true,
    partDescriptorActions: partDescriptorActionsFn,
  };
};
